import { useCallback, useEffect, useRef, useState } from "react";
import GetSchema, { ISchema, ISchemaFilter, ISchemaProperty } from "../entityComponents/Schema";
import { httpPost } from "../../../modules/Backend";
import ListFilters from "./ListFilters";
import { IFilter, useFilters } from "../../../hooks/UseFilters";
import { globalAdditionSignal, globalDeletionSignal, globalListReloadSignal, globalUpdateResultsSignal, movingSidePanelSignal } from "../layout/Layout";
import { TableVirtuoso } from 'react-virtuoso'
import { Checkbox } from "react-aria-components";
import CheckIcon from "../../../assets/icons/icon-check-white.svg"
import { PropertyTypes } from "../../../enums/PropertyTypes";
import Pill, { IPillLookup } from "../Pill";
import { showHeader } from "../layout/Header";
import IconSearch from "../../../assets/icons/icon-search-grey.svg"
import { FilterMatchType } from "../../../enums/FilterMatchType";
import { EntityNames } from "../../../enums/EntityNames";
import { RelatedFilterValidation } from "../../../enums/RelatedFilterValidation";
import { useSignals } from "@preact/signals-react/runtime";
import ListActions from "./ListActions";
import { PageSizes } from "../../../enums/PageSizes";
import { EntityViewTypes } from "../../../enums/EntityViewTypes";
import { throttle } from "../../../helpers/throttler";
import { useKeyBind } from "../../../modules/keybinds/useKeyBind";
import KeyBinds, { KeyActions as Actions } from "../../../modules/keybinds/keyBindList";
import { handleCloseSidePanel } from "../layout/SidePanel/SidePanel";
import Percentage from "../Percentage";
import DownloadViewWidget from "../DownloadViewWidget";
import { HistoryActionType } from "../../../enums/Domain/HistoryActionType";
import { displayDateOnly as displayDateOnly, displayDateTime, isValidIsoDateString } from "../../../helpers/DateHelper";
import { PermissionContextType, usePermissions } from "../../../modules/auth/Permissions/PermissionContext";
import { useMainConfig } from "../../../modules/config/MainConfigContext";
import useRecallNotifications, { newRecallIdsSignal } from "../../../hooks/useRecallNotifications";

export interface IProps {
    entityName: string,
    parentEntityName: string | undefined,
    entityId: string | undefined,
    enableFilters: boolean
    forcedFilters?: IFilter[]
    searchType: EntityViewTypes.list | EntityViewTypes.search | EntityViewTypes.writableList,
    cantView?: boolean,
    onItemClick?: Function | undefined
    tabView: boolean,
    canAdd: boolean | ((ctx: PermissionContextType, accountId: string | undefined) => boolean),
    pollTime?: number | undefined,
    onLoadCallback?: Function,
    alwaysShowheader?: boolean | undefined
}

export interface ISelected {
    id: string
    entityName: string
}

export default function List({ entityId, entityName, parentEntityName, enableFilters, forcedFilters, cantView, searchType, tabView, canAdd, pollTime, onLoadCallback, alwaysShowheader }: IProps) {
    const throttleMs = 300;
    const prevRef = useRef<Function | undefined>();
    const nextRef = useRef<Function | undefined>();
    const listRef = useRef<any>(null);
    const permissionsContext = usePermissions();

    const throttlePrev = useCallback(
        throttle((event: KeyboardEvent) => {
            if (prevRef.current) {
                prevRef.current();
            }
        }, throttleMs),
        [throttleMs]
    );

    const throttleNext = useCallback(
        throttle((event: KeyboardEvent) => {
            if (nextRef.current) {
                nextRef.current();
            }
        }, throttleMs),
        [throttleMs]
    );

    useSignals()
    useKeyBind(KeyBinds[Actions.MoveToPreviousListItem], throttlePrev)
    useKeyBind(KeyBinds[Actions.MoveToNextListItem], throttleNext)
    useKeyBind(KeyBinds[Actions.CloseSidePanel], handleCloseSidePanel)

    const [data, setData] = useState<any>(undefined);
    const { config } = useMainConfig();
    const schema: ISchema = GetSchema(entityName, config || undefined);
    
    // Filter actions by permissions to determine if checkboxes should be shown
    const filteredActions = schema.listActions?.filter(action => 
        action.permissionToView === undefined || action.permissionToView(permissionsContext)
    ) ?? [];
    const hasActions = filteredActions.length > 0;

    // Auto-load if: all filters are optional, OR filters are disabled, OR forcedFilters are provided (parent context)
    // If schema.filters is undefined, don't auto-load (conservative approach when filters are enabled)
    const autoLoad: boolean = !enableFilters || 
                              (forcedFilters && forcedFilters.length > 0) || 
                              !!(schema.filters && (schema.filters.length === 0 || schema.filters.every(x => x.required === false)));
    const { onFiltersChange, filters, clearFilters, setFilters } = useFilters({ schemaFilters: schema.filters });
    const pageSize = PageSizes.large;
    const [keysets, setKeysets] = useState<any>();
    const [fetchQueued, setFetchQueued] = useState<boolean>(autoLoad);
    const [selected, setSelected] = useState<ISelected[]>([])
    const [viewedItem, setViewedItem] = useState<string>()
    const [resultsKey] = useState<string>(schema.resultsKey ?? entityName);
    const [isNewTimePeriodMinutes, setIsNewTimePeriodMinutes] = useState<number | undefined>(undefined);

    // Set up recall notifications for operator and customer recalls
    const isRecallList = entityName === EntityNames.operatorRecalls || entityName === EntityNames.customerRecalls;
    useRecallNotifications({
        currentData: data?.[resultsKey],
        sortByDate: false,
        isNewTimePeriodMinutes: isNewTimePeriodMinutes,
        onDataUpdate: (updatedData) => {
            setData({ [resultsKey]: updatedData });
        }
    });
    
    // Note: We access newRecallIdsSignal.value directly in the render below
    // useSignals() makes this reactive, so the component will re-render when the signal changes
    
    const [showLoader, setShowLoader] = useState<boolean>(false)
    const [allDataLoaded, setAllDataLoaded] = useState<boolean>(false);

    async function getData(filters: IFilter[], keysets: any[], noLoader?: boolean | undefined, isPolled?: boolean | undefined, replace?: boolean | undefined, add?: boolean | undefined) {
        try {
            // When data is loaded in response to realtime update, we want to silently load and replace the data
            // In this instance we don't want the loader to appear 'randomly'
            const timeout = setTimeout(() => {
                if (noLoader) {
                    return;
                }
                setShowLoader(true);
            }, 200)
            setFetchQueued(false);
            const path = `${entityName}/${searchType}`;
            const result = await httpPost(`api/${path}`, {
                id: entityId,
                entityName: parentEntityName,
                filters: filters.concat(forcedFilters ?? []),
                keysets: keysets,
                take: pageSize,
                replaceId: replace ? viewedItem : null,
                additionId: add ? globalAdditionSignal.value?.id : null,
                isPolled: isPolled ?? false
            });

            if (result.data.keysets) {
                setKeysets(result.data.keysets)
            }
            
            if (isRecallList && result.data.isNewTimePeriodMinutes !== undefined && result.data.isNewTimePeriodMinutes !== null) {
                setIsNewTimePeriodMinutes(result.data.IsNewTimePeriodMinutes);
            }
            setShowLoader(false)
            clearTimeout(timeout)
            const newData = { [resultsKey]: result.data[resultsKey] };

            // Check if the response itself indicates that no more data is avaialble
            if (result.data.allDataLoaded) {
                setAllDataLoaded(true);
            }

            // If no existing data or this is a replacement/addition, set the data directly
            if (!data || replace || add) {
                if (!isPolled && !replace && !add && result.data[resultsKey].length < pageSize) {
                    setAllDataLoaded(true);
                }
                if (onLoadCallback) {
                    onLoadCallback(newData[resultsKey]);
                }
                setData(newData);
                return newData[resultsKey];
            }

            // For polled updates, merge the results
            if (isPolled) {
                const mergeResult = mergeResults(result);
                return mergeResult.data;
            }

            // For normal pagination, merge and append new results by ID
            const existingItems = new Map(data[resultsKey].map((item: any) => [item.id, item]));

            // Update or add new items
            for (const newItem of newData[resultsKey]) {
                existingItems.set(newItem.id, newItem);
            }

            const combinedData = {
                [resultsKey]: Array.from(existingItems.values())
            };
            setData(combinedData);

            if (!isPolled && newData[resultsKey].length < pageSize) {
                setAllDataLoaded(true);
            }
            return combinedData[resultsKey];

        }
        catch (err) {
        }
    }

    function mergeResults(result: any) {
        const existingItems = new Map(data[resultsKey].map((item: any) => [item.id, item]));
        let hasNewIds = false;

        // Process incoming data
        for (const newItem of result.data[resultsKey]) {
            if (!existingItems.has(newItem.id)) {
                hasNewIds = true;
            }
            existingItems.set(newItem.id, newItem); // Replace or add item
        }

        let newData;
        if (entityName === EntityNames.operatorRecalls || entityName === EntityNames.customerRecalls) {
            // Get all existing IDs that aren't in the new result set
            const newItemIds = new Set(result.data[resultsKey].map((item: any) => item.id));
            const existingOnlyItems = data[resultsKey]
                .filter((item: any) => !newItemIds.has(item.id));

            // Combine existing items with new items, maintaining new order
            newData = [
                ...result.data[resultsKey].map((item: any) => existingItems.get(item.id)),
                ...existingOnlyItems
            ].filter((item: any) => item !== undefined);
        } else {
            newData = Array.from(existingItems.values());
        }

        setData({ [resultsKey]: newData });
        return { data: newData, hasNewIds };
    }

    function viewItem(data: any, replace?: boolean | undefined) {

        if (!replace && data && movingSidePanelSignal.value.show === true &&
            movingSidePanelSignal.value && movingSidePanelSignal.value.id === data.id) {
            return;
        }

        if (data && schema.GetSidePanelComponent) {
            const sidePanelComponent = schema.GetSidePanelComponent({ ...data, entityName: data.entityName ?? entityName }, false, data.entityName ?? entityName)
            movingSidePanelSignal.value = {
                show: true,
                id: data.id,
                component: sidePanelComponent
            }
        }
    }

    function handlePrimaryFilterSubmit(filter: ISchemaFilter, value: string) {
        const updatedFilters = [...filters]
        let valueToChange = updatedFilters.find(x => x.propertyName == filter.PropertyName)
        if (valueToChange) {
            if (!value) {
                const index = updatedFilters.indexOf(valueToChange);
                updatedFilters.splice(index, 1);
            } else {
                valueToChange.value = value?.toString()
            }
        }
        else if (value) {
            updatedFilters.push({
                propertyName: filter.PropertyName,
                required: filter.required ?? false,
                value: value?.toString(),
                type: filter.propertyType,
                matchType: filter.matchType ?? FilterMatchType.Equal
            } as IFilter)
        }

        if (updatedFilters.length > 0 || autoLoad) {
            setSelected([])
            setData(undefined)
            setShowLoader(true)
            setKeysets([]);
            setFilters(updatedFilters)
            setFetchQueued(true)
            setAllDataLoaded(false);
        }

        if (updatedFilters.length === 0) {
            setSelected([])
            setData(undefined)
            setShowLoader(true)
            setKeysets([]);
            setFilters(updatedFilters)
            setFetchQueued(true)
            setAllDataLoaded(false);
        }
    }

    function handleFilterSubmit() {
        setSelected([])
        setData(undefined)
        setShowLoader(true)
        setKeysets([]);
        setFetchQueued(true)
        setAllDataLoaded(false);
    }

    function clearConflictingFilters(filters: IFilter[]) {
        const filtersToApply: IFilter[] = [...filters];
        for (const filter of filters) {
            const match = schema.filters?.find(x => x.PropertyName === filter.propertyName);
            if (match) {
                const isExclusive = match.relatedFilter && match.relatedFilterValidation == RelatedFilterValidation.MustNotHaveValue;
                if (isExclusive) {
                    const conflictingFilterIndex = filtersToApply.findIndex(x => x.propertyName === match.relatedFilter);
                    if (conflictingFilterIndex !== -1) {
                        const matchIndex = filtersToApply.findIndex(x => x.propertyName === match.PropertyName);
                        filtersToApply.splice(matchIndex, 1);
                    }
                }
            }
        }
        setFilters(filtersToApply);
        return filtersToApply;
    }

    function handleClearFilters() {
        setFetchQueued(autoLoad)
        setSelected([])
        setKeysets([]);
        setAllDataLoaded(false);
        setData(undefined)
        setKeysets([])
        clearFilters()
    }

    function handleSelectionChange(x: ISelected) {
        const newSelected = [...selected]
        const index = newSelected.map(e => e.id).indexOf(x.id)
        if (index === -1) {
            newSelected.push(x)
        } else {
            newSelected.splice(index, 1)
        }
        setSelected(newSelected)
    }

    function handleScroll(event: React.WheelEvent<HTMLDivElement>) {
        if (!alwaysShowheader) {
            event.deltaY < 0 ? showHeader.value = true : showHeader.value = false;
        }
    }

    async function getDataWithParams(noLoader?: boolean | undefined) {
        const filtersToApply = clearConflictingFilters(filters);
        await getData(filtersToApply, keysets, noLoader);
    }

    async function getDataWithParamsPolled(filters: IFilter[], keysets: any[]) {
        const filtersToApply = clearConflictingFilters(filters);
        await getData(filtersToApply, keysets, true, true);
    }

    function nextItem(currentItem: string | undefined) {
        if (data && data![resultsKey]) {
            const dataArray = data![resultsKey];
            const index = currentItem ? dataArray.findIndex((x: any) => x.id === currentItem) : -1;
            return index === dataArray.length - 1 ? undefined : () => {
                viewItem(dataArray[index + 1]);
                scrollTo(index, "center");
            };
        }
    }

    function prevItem(currentItem: string | undefined) {
        if (currentItem && data && data![resultsKey]) {
            const dataArray = data[resultsKey];
            const index = dataArray.findIndex((x: any) => x.id === currentItem);
            return index === 0 ? undefined : () => {
                viewItem(dataArray[index - 1]);
                scrollTo(index, "center");
            };
        }
    }

    function handleRowClicked(item: any) {
        if (cantView) return;

        viewItem(item);
    }

    const scrollTo = (index: number, align: "start" | "end" | "center") => {
        listRef.current!.scrollToIndex({
            index: index,
            align: align,
            behavior: "smooth",
        });
    };

    function handleEndReached(index: number) {
        if (allDataLoaded || fetchQueued) {
            return;
        }
        // Ensure we have keysets from the last response before loading more
        if (keysets && keysets.length > 0) {
            setFetchQueued(true);
        }
    }

    useEffect(() => {
        if (fetchQueued) {
            getDataWithParams();
        }
    }, [fetchQueued]);

    useEffect(() => {
        prevRef.current = prevItem(viewedItem);
        nextRef.current = nextItem(viewedItem);
    }, [prevItem, nextItem, viewedItem])

    useEffect(() => {
        if (movingSidePanelSignal.value.id && data) {
            const index = data[resultsKey]?.findIndex((x: any) => x.id === movingSidePanelSignal.value.id);
            if (index !== -1) {
                scrollTo(index, "center");
                setViewedItem(movingSidePanelSignal.value.id);
            }
        }
        else {
            setViewedItem("");
        }
    }, [movingSidePanelSignal.value]);

    useEffect(() => {
        if (globalDeletionSignal.value.id && data) {
            const updatedData = data[resultsKey].filter((item: any) => item.id !== globalDeletionSignal.value.id);
            setData({ [resultsKey]: updatedData });
            movingSidePanelSignal.value = { show: false, id: undefined, component: undefined }
            globalDeletionSignal.value = { id: undefined }
        }
    }, [globalDeletionSignal.value])

    useEffect(() => {
        async function handleAdded() {
            if (globalAdditionSignal.value?.id && data) {
                const capturedData = await getData(filters, keysets, true, false, false, true);
                if (capturedData && capturedData.length > 0) {
                    const newItem = capturedData[capturedData.length - 1];
                    const updatedData = [...data[resultsKey], newItem];
                    setData((prevData: any) => ({
                        ...prevData,
                        [resultsKey]: updatedData
                    }));
                }
            }
        }
        handleAdded()
    }, [globalAdditionSignal.value])

    useEffect(() => {
        if (listRef.current && data && globalAdditionSignal.value?.id) {
            const newItem = data[resultsKey].find((x: any) => x.id === globalAdditionSignal.value.id);
            if (newItem) {
                viewItem(newItem);
                globalAdditionSignal.value = { id: undefined };
            }
        }
    }, [data]);

    useEffect(() => {
        if (globalListReloadSignal.value.hashValue) {
            getDataWithParams(true);
            globalListReloadSignal.value = { hashValue: undefined }
        }
    }, [globalListReloadSignal.value.hashValue])

    useEffect(() => {
        const signal = globalUpdateResultsSignal.value;
        // Does the update affect a specific instance by id?
        if (data && signal.updateChanges && signal.id) {
            const updatedData = { ...data };
            const itemToUpdate = updatedData[resultsKey].find((x: any) => x.id === signal.id);
            if (itemToUpdate) {
                for (const key in signal.updateChanges) {
                    if (key in itemToUpdate) {
                        itemToUpdate[key] = signal.updateChanges[key];
                    }
                }
                setData(updatedData);
                // Dont clear the signal as side panel may also react to this change
            }
        }
    }, [globalUpdateResultsSignal.value]);



    useEffect(() => {
        if (alwaysShowheader) {
            showHeader.value = true;
        }
    }, [])

    return (schema && <div className="list__wrapper">
        {enableFilters && <ListFilters
            submit={handleFilterSubmit}
            primaryFilterSubmit={handlePrimaryFilterSubmit}
            filterValues={filters}
            primaryFilter={schema.primaryFilter}
            filters={tabView ? schema.filters?.filter(x => x.showInTab === true) : schema.filters}
            onFiltersChange={onFiltersChange}
            entityName={entityName}
            parentId={entityId}
            clearFilters={() => { handleClearFilters() }}
            canAdd={canAdd}
            loading={showLoader} />}

        {selected.length > 0 && <ListActions selected={selected} clearFunc={() => setSelected([])} actions={schema.listActions!} />}

        <div className="list" data-testid="list-wrapper">
            {(data && data[resultsKey] != undefined && data[resultsKey]?.length > 0) && renderListTable()}
            {!data && renderEmptyList()}
            {data?.[resultsKey]?.length === 0 && renderNoResultList()}
        </div>
    </div>
    )

    function renderListTable() {
        const footerLoaderRowCount = 6;
        const showFooterLoaders = showLoader && !allDataLoaded;

        return (
            <>
                <TableVirtuoso
                    ref={listRef}
                    className="react-aria-Table"
                    endReached={allDataLoaded ? undefined : handleEndReached}
                    followOutput={false}
                    data={data[resultsKey]} onWheel={(x) => handleScroll(x)}
                    initialItemCount={data[resultsKey]?.length < pageSize || allDataLoaded ? data[resultsKey]?.length : undefined}
                    overscan={{ main: 100, reverse: 100 }}
                    fixedHeaderContent={() => (<tr className="">
                        {hasActions && <th></th>}
                        {schema.props.filter(x => x.showInList === true).map(prop => (<>
                            <th><p className="small" style={{ margin: 0 }}>{prop.displayName}</p></th>
                        </>))}
                    </tr>)}
                    itemContent={(index, item) => (
                        <>
                            {hasActions &&
                                <td 
                                    className="list__checkbox"
                                    data-new-recall={isRecallList && newRecallIdsSignal.value.has(item.id) ? 'true' : undefined}
                                >
                                    <Checkbox slot="selection" onChange={() => { handleSelectionChange({ id: item.id, entityName: item.entityName }) }}>
                                        <div className={`input-checkbox  ${selected.map(x => x.id).includes(item.id) ? "input-checkbox--selected" : "input-checkbox--unselected"}`}>
                                            <img alt="" src={CheckIcon} />
                                        </div>
                                    </Checkbox>
                                </td>
                            }
                            {schema.props.filter(x => x.showInList === true).map((prop, propIndex) => {
                                const isNew = isRecallList && newRecallIdsSignal.value.has(item.id);
                                const isFirstCell = propIndex === 0;
                                return (
                                    <td 
                                        key={propIndex}
                                        className={`list__row-item ${isNew ? 'list__row-item--fade-in list__row-item--new-recall' : ''}`}
                                        data-selected={viewedItem === item.id}
                                        data-new-recall={isNew ? 'true' : undefined}
                                        onClick={() => handleRowClicked(item)}
                                    >
                                        {isNew && isFirstCell && (
                                            <div className="list__new-request-tag">
                                                <p>NEW</p>
                                            </div>
                                        )}
                                        {getCellComponent(item, prop, entityName)}
                                    </td>
                                );
                            })}
                        </>
                    )}
                    fixedFooterContent={() => <tr style={{ margin: 0, padding: 0 }}></tr>}
                    components={{
                        TableFoot: () => (<>
                            {showFooterLoaders ? (
                                [...Array(footerLoaderRowCount)].map((e, i) => (
                                    <tr key={i}>
                                        {hasActions && <td></td>}
                                        {schema.props.filter(x => x.showInList === true).map(prop => (
                                            <td key={prop.name}>
                                                <div className="loaders loaders__list"></div>
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : (
                                <tr style={{ height: "20px", pointerEvents: "none", background: "transparent" }}>
                                    <td
                                        colSpan={1000}
                                        style={{ height: "20px", padding: 0, border: "none", background: "transparent" }}
                                    ></td>
                                </tr>
                            )}
                            {/* Bottom spacer to prevent scrolling to absolute bottom (prevents jiggling) */}
                            <tr style={{ height: '8px', pointerEvents: 'none', background: 'transparent' }}>
                                <td colSpan={1000} style={{ height: '8px', padding: 0, border: 'none', background: 'transparent' }}></td>
                            </tr>
                        </>)
                    }}
                />
            </>
        )
    }

    function renderEmptyList() {
        return (
            <div>
                <div className="react-aria-Table">
                    <table >
                        {hasActions && <th></th>}
                        {schema.props.filter(x => x.showInList === true).map(prop => (<>
                            <th><p className="small">{prop.displayName}</p></th>
                        </>))}
                        {showLoader && [...Array(6)].map((e, i) => (
                            <tr key={i}>
                                {hasActions && <td></td>}
                                {schema.props.filter(x => x.showInList === true).map(prop => (
                                    <td key={prop.name}>
                                        <div className="loaders loaders__list"></div>
                                    </td>
                                ))}
                            </tr>))}
                    </table>
                </div>
                {!showLoader && !autoLoad && <div className="list--empty">
                    <div className="display--flex flex"><img alt="" src={IconSearch} /></div>
                    <p>Begin by typing into the search bar or selecting some filters</p>
                </div>}
            </div>
        )
    }

    function renderNoResultList() {
        return (
            <div>
                <div className="react-aria-Table">
                    <table >
                        {hasActions && <th></th>}
                        {schema.props.filter(x => x.showInList === true).map(prop => (<>
                            <th><p className="small">{prop.displayName}</p></th>
                        </>))}
                        {showLoader && [...Array(6)].map((e, i) => (
                            <tr key={i}>
                                {hasActions && <td></td>}
                                {schema.props.filter(x => x.showInList === true).map(prop => (
                                    <td key={prop.name}>
                                        <div className="loaders loaders__list"></div>
                                    </td>
                                ))}
                            </tr>))}
                    </table>
                </div>
                {!showLoader && <div className="list--empty">
                    <div className="display--flex flex"><img alt="" src={IconSearch} /></div>
                    <p>No Results</p>
                </div>}
            </div>
        )
    }
}

export function getCellComponent(x: any, prop: ISchemaProperty, entiyName: string) {
    const paragraphStyle = { margin: 0 };

    if (x['action'] === HistoryActionType.location && ['from', 'to'].includes(prop.name)) {
        return <p className="breadcrumb" style={paragraphStyle}>{x[prop.name]}</p>
    }

    if (x['action'] === HistoryActionType.status && ['from'].includes(prop.name)) {
        const lookup = {
            statusName: x['statusLookupName'],
            statusKey: x['from']

        } as IPillLookup;
        return <Pill statusDto={undefined} pillLookup={lookup} />
    }

    if (x['action'] === HistoryActionType.status && ['to'].includes(prop.name)) {
        const lookup = {
            statusName: x['statusLookupName'],
            statusKey: x['to']

        } as IPillLookup;
        return <Pill statusDto={undefined} pillLookup={lookup} />
    }

    if (prop.type === PropertyTypes.download) {
        return <DownloadViewWidget id={x.id} entityName={entiyName} />
    }

    if (prop.valueFormatter) {
        return <p className="small small--2" style={paragraphStyle}>{prop.valueFormatter(x[prop.name])}</p>
    }

    if (isValidIsoDateString(x[prop.name])) {
        return <p className="small small--2" style={paragraphStyle}>{displayDateTime(x[prop.name])}</p>
    }

    if ((x[prop.name] === undefined || x[prop.name] === null) && prop.defaultDisplayValue) {
        return <p className="small small--2" style={paragraphStyle}>{prop.defaultDisplayValue}</p>
    }

    if (x[prop.name] === null || x[prop.name] === undefined)
        return <></>

    switch (prop.type) {
        case PropertyTypes.breadcrumb:
            return <p className="breadcrumb" style={paragraphStyle}>{x[prop.name]}</p>

        case PropertyTypes.status:
            return <Pill statusDto={x[prop.name]} />

        case PropertyTypes.dateTime:
            return <p className="small small--2" style={paragraphStyle}>{displayDateTime(x[prop.name])}</p>

        case PropertyTypes.dateOnly:
            return <p className="small small--2" style={paragraphStyle}>{displayDateOnly(x[prop.name])}</p>

        case PropertyTypes.percentage:
            return <div className="pallet-stats__widget">
                <Percentage amount={parseInt(x[prop.name])} />
            </div>

        default:
            return <p className="small small--2" style={paragraphStyle}>{x[prop.name]}</p>
    }
}